import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InfoIcon, ArrowLeft, ArrowRight, PlusCircle, X, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateIP() {
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [newPatent, setNewPatent] = useState('');
  const [newTech, setNewTech] = useState('');
  
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      ip: saved?.ip || {
        patentStatus: 'none',
        patentNumbers: [],
        proprietaryTechnology: '',
        technicalArchitecture: '',
        developmentStage: 'idea',
        techStack: [],
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/gtm');
  };

  const handleNext = () => {
    navigate('/pitch/new/risks');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const addArrayItem = (field: string, value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      ip: {
        ...prev.ip,
        [field]: [...prev.ip[field as keyof typeof prev.ip] as string[], value.trim()]
      }
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      ip: {
        ...prev.ip,
        [field]: (prev.ip[field as keyof typeof prev.ip] as string[]).filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">IP & Technology</h1>
            <p className="text-muted-foreground mt-1">Step 6 of 8 · Your technical foundation and intellectual property</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Technology & IP Strategy</CardTitle>
            <CardDescription>
              For pre-seed, investors want to know what's proprietary and how defensible your technology is.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-indigo-50 border-indigo-200">
              <InfoIcon className="h-4 w-4 text-indigo-600" />
              <AlertDescription className="text-indigo-700">
                Even without patents, you can have trade secrets, proprietary algorithms, or unique data.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Patent Status</Label>
              <Select 
                value={form.ip.patentStatus}
                onValueChange={(value) => setForm({
                  ...form,
                  ip: {
                    ...form.ip,
                    patentStatus: value
                  }
                })}
              >
                <SelectTrigger className="text-base py-6">
                  <SelectValue placeholder="Select patent status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No patents</SelectItem>
                  <SelectItem value="filed">Filed</SelectItem>
                  <SelectItem value="provisional">Provisional Patent</SelectItem>
                  <SelectItem value="granted">Granted</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.ip.patentStatus !== 'none' && (
              <div className="space-y-3">
                <Label className="text-lg font-medium">Patent Numbers</Label>
                <div className="flex gap-2">
                  <Input
                    value={newPatent}
                    onChange={e => setNewPatent(e.target.value)}
                    placeholder="Add patent number or application ID"
                    className="text-base py-6"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addArrayItem('patentNumbers', newPatent);
                        setNewPatent('');
                      }
                    }}
                  />
                  <Button 
                    type="button" 
                    onClick={() => {
                      addArrayItem('patentNumbers', newPatent);
                      setNewPatent('');
                    }}
                    className="px-6"
                  >
                    <PlusCircle className="h-5 w-5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {form.ip.patentNumbers.map((patent, i) => (
                    <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                      <span className="text-sm">{patent}</span>
                      <X 
                        className="h-4 w-4 cursor-pointer hover:text-red-500" 
                        onClick={() => removeArrayItem('patentNumbers', i)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Label className="text-lg font-medium">Proprietary Technology</Label>
              <Textarea
                value={form.ip.proprietaryTechnology}
                onChange={e => setForm({
                  ...form,
                  ip: {
                    ...form.ip,
                    proprietaryTechnology: e.target.value
                  }
                })}
                placeholder="What technology do you own or have exclusive rights to? Algorithms? Data sets? Hardware? Trade secrets?"
                className="min-h-[120px] text-base p-4"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Technical Architecture</Label>
              <Textarea
                value={form.ip.technicalArchitecture}
                onChange={e => setForm({
                  ...form,
                  ip: {
                    ...form.ip,
                    technicalArchitecture: e.target.value
                  }
                })}
                placeholder="High-level technical architecture. Frontend, backend, databases, APIs, infrastructure, etc."
                className="min-h-[120px] text-base p-4"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Development Stage</Label>
              <Select 
                value={form.ip.developmentStage}
                onValueChange={(value) => setForm({
                  ...form,
                  ip: {
                    ...form.ip,
                    developmentStage: value
                  }
                })}
              >
                <SelectTrigger className="text-base py-6">
                  <SelectValue placeholder="Select development stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="idea">Idea / Concept</SelectItem>
                  <SelectItem value="prototype">Prototype</SelectItem>
                  <SelectItem value="alpha">Alpha</SelectItem>
                  <SelectItem value="beta">Beta</SelectItem>
                  <SelectItem value="live">Live / Launched</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Tech Stack</Label>
              <div className="flex gap-2">
                <Input
                  value={newTech}
                  onChange={e => setNewTech(e.target.value)}
                  placeholder="e.g., React, Python, AWS, PostgreSQL"
                  className="text-base py-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addArrayItem('techStack', newTech);
                      setNewTech('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('techStack', newTech);
                    setNewTech('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.ip.techStack.map((tech, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{tech}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('techStack', i)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: Go-to-Market
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: Risks
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}